import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { FaFileAlt, FaSpinner, FaPlusCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import "./MyOrders.css";

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const SERVER_URL = process.env.REACT_APP_SERVER_URL;
        const response = await axios.get(`${SERVER_URL}/api/my-orders/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        setOrders(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load orders. Please try again later.");
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  if (loading) {
    return (
      <div className="my-orders-container">
        <div className="loading-state" role="status" aria-live="polite">
          <FaSpinner className="spinner-icon" aria-hidden="true" />
          <span>Loading your orders...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-orders-container">
        <div className="error-state" role="alert" aria-live="assertive">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-orders-container">
      <Helmet>
        <title>My Orders – LingoLab</title>
        <meta
          name="description"
          content="View and track your LingoLab translation orders. Check order status, details, and manage your video translation requests."
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://lingolab.ca/my-orders" />
        <meta property="og:title" content="My Orders – LingoLab" />
        <meta
          property="og:description"
          content="View and track your LingoLab translation orders. Check order status, details, and manage your video translation requests."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lingolab.ca/my-orders" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@LingLabAI" />
        <meta name="twitter:title" content="My Orders – LingoLab" />
        <meta
          name="twitter:description"
          content="View and track your LingoLab translation orders. Check order status, details, and manage your video translation requests."
        />
        <meta name="twitter:image" content="https://lingolab.ca/ll-X.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "LingoLab",
              "url": "https://lingolab.ca/",
              "logo": "https://lingolab.ca/ll-X.png",
              "sameAs": [
                "https://twitter.com/LingLabAI"
              ],
              "description": "LingoLab uses advanced AI to translate and localize your videos into 80+ languages, preserving the original voice and syncing lips for a native look.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@lingolab.ca",
                "contactType": "customer support",
                "url": "https://lingolab.ca/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <motion.header
        className="my-orders-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.4,
          ease: "easeOut",
        }}
      >
        <h1 className="my-orders-title">My Orders</h1>
        <p className="my-orders-subtitle">Track Your Translation Requests</p>
        <div className="header-divider"></div>
      </motion.header>

      <div className="my-orders-list">
        {orders.length === 0 ? (
          <motion.div
            className="no-orders-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.4 }}
            role="region"
            aria-labelledby="no-orders-title"
          >
            <FaPlusCircle className="no-orders-icon" aria-hidden="true" />
            <p className="no-orders-text" id="no-orders-title">
              You have no orders yet.{" "}
              <Link
                to="/translation-order"
                className="no-orders-link"
                aria-label="Start a new translation order"
                onTouchStart={() => {}}
              >
                Start a new translation.
              </Link>
            </p>
          </motion.div>
        ) : (
          orders.map((order, index) => (
            <motion.div
              key={order.id}
              className="order-card"
              role="article"
              aria-labelledby={`order-title-${order.id}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: shouldReduceMotion ? 0 : 0.3,
                delay: index * 0.1,
              }}
              whileHover={
                shouldReduceMotion
                  ? {}
                  : { y: -5, boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)" }
              }
            >
              <FaFileAlt className="order-icon" aria-hidden="true" />
              <div className="order-content">
                <div className="order-field">
                  <span className="order-label">Video URL:</span>
                  <a
                    href={order.video.original_video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="order-url"
                    aria-label={`View original video for order ${order.id}`}
                    onTouchStart={() => {}}
                  >
                    {order.video.original_video_url}
                  </a>
                </div>
                <div className="order-field">
                  <span className="order-label">Created:</span>
                  <span>{new Date(order.created_at).toLocaleString()}</span>
                </div>
                <div className="order-field">
                  <span className="order-label">Last Updated:</span>
                  <span>{new Date(order.last_change_at).toLocaleString()}</span>
                </div>
                <div className="order-field order-field-status">
                  <span className="order-label">Status:</span>
                  <span
                    className={`order-status status-${order.status.toLowerCase()}`}
                  >
                    {order.status}
                  </span>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default MyOrders;
